/**
 * Screen-local API module for the Daily Spend & Revenue report (task 055-03;
 * epic 055 README decision 1). Deliberately outside `lib/api.ts` (ADR-010
 * gap #2 -> A2 (b)), the same screen-local pattern as `reports-api.ts`: same
 * base-URL expression and `{ data }` envelope handling.
 *
 * The stats contract mirrors the api-local DTOs pinned in task 055-02's
 * Contract block, as landed in
 * `packages/api/src/modules/reporting/daily-spend-stats.service.ts` (ADR-010
 * A3: the SPA mirrors api-local report DTOs; `zod` is not a dependency of
 * this package). The entry endpoints are task 055-01's admin CRUD:
 * `PUT /admin/daily-spend-entries/<date>` upserts `{ amount }` and returns
 * the envelope, `DELETE /admin/daily-spend-entries/<date>` returns 204.
 */

import { apiFetch } from '../../lib/api';

/** One grid row — every cell day-grain, her sheet's arithmetic (decision 6). */
export interface DailySpendDayRow {
  /** yyyy-MM-dd in DISTRIBUTION_TZ — every day in [from, to]. */
  date: string;
  /** Day grain: click_out accepts x price + lead_post day-unique billable x price. */
  customPageRevenue: number;
  /** Day grain: sum of co_op campaigns, day-unique billable x price. */
  coopRevenue: number;
  /** COUNT(DISTINCT email_hash) over lead.created_at in the day, ALL styles. */
  leads: number;
  /** daily_spend_entry.amount; null = no entry (blank cell + editor). */
  displayAdSpend: number | null;
  /** Day grain: sum of sites, day-unique through the site's CTO links x site.cpl. */
  ctoAdSpend: number;
  /** round2(custom + coop). */
  grossRevenue: number;
  /** round(0.9 x gross, 2) — her "Total Revenue". */
  netRevenue: number;
  /** ctoAdSpend + (displayAdSpend ?? 0). */
  totalSpend: number;
  /** round2(gross / leads); null when leads = 0. */
  epl: number | null;
  coOpEpl: number | null;
  customPgEpl: number | null;
  /** round2(totalSpend / leads). */
  cpl: number | null;
  /**
   * round2((coopRevenue / 2 / leads) * 100) — her "Co-reg Plus Lead %", already
   * a percent (230.66, not 2.31); null when leads = 0.
   */
  coregPlusLeadPct: number | null;
  /** Sum of the co_op campaigns' day-unique billable — her "Total Unique Leads". */
  leadsDelivered: number;
}

/** One campaign column day cell; null = the campaign was not running (renders en dash). */
export interface DailySpendCampaignDay {
  date: string;
  leads: number | null;
  capped: boolean;
}

/** One per-co-op-campaign column block of the grid. */
export interface DailySpendCampaign {
  campaignId: string;
  name: string;
  pricePerLead: number | null;
  dailyCap: number | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  /** Month-grain distinct — the billing number (column footer). */
  monthUniqueLeads: number;
  /** Exactly one per day in [from, to], ordered. */
  days: DailySpendCampaignDay[];
}

/** One per-traffic-source row — the cto-stats source rule + the site terms. */
export interface DailySpendSourceRow {
  /** site.id::text, site.site_id, site.label. */
  id: string;
  siteCode: string;
  label: string;
  /** Month grain; unique = Q82 across the source's links. */
  rawLeads: number;
  uniqueLeads: number;
  /** unique x cpl; null when cpl is null. */
  cpl: number | null;
  totalSpend: number | null;
  budget: number | null;
  leadLimit: number | null;
  /** Sorted yyyy-MM-dd (may be []). */
  invoiceDates: string[];
}

/** The Facebook row of the Traffic sources block (087-02, QA-27): every Paid Social sign-up in the range. */
export interface DailySpendPaidSocialRow {
  /** COUNT(*) over the range. */
  rawLeads: number;
  /** Distinct addresses over the range (the CTO rows' range grain). */
  uniqueLeads: number;
  /** Sum of daily_spend_entry over the range (= totals.displayAdSpend); null when no entry exists in range. */
  spend: number | null;
  /** round2(spend / uniqueLeads); null when spend is null or uniqueLeads = 0. */
  cpl: number | null;
}

/** Both dedup grains side by side (decision 4): raw = sum of days; deduped = month. */
export interface DailySpendTotals {
  leadsRaw: number;
  leadsDeduped: number;
  customPageRevenue: number;
  customPageRevenueDeduped: number;
  coopRevenue: number;
  coopRevenueDeduped: number;
  ctoAdSpend: number;
  ctoAdSpendDeduped: number;
  /** Sum of entries in range. */
  displayAdSpend: number;
  grossRevenue: number;
  netRevenue: number;
  netRevenueDeduped: number;
  /** round2(netRevenue / (displayAdSpend + ctoAdSpend) x 100); null when spend = 0. */
  roiPct: number | null;
}

/** Response of GET /admin/reports/daily-spend-stats — the whole dashboard. */
export interface DailySpendStatsResponse {
  from: string;
  to: string;
  timezone: string;
  includeTest: boolean;
  /**
   * The effective source filter key (task 094-03, D3) echoed back for the UI:
   * `'all'` when unfiltered, else the pseudo-key (`'facebook'`,
   * `'unattributed'`) or the `site.site_id` the request carried.
   */
  source: string;
  /** Every calendar day in [from, to], ordered. */
  days: DailySpendDayRow[];
  /** co_op campaigns with >= 1 billable send in range, ORDER BY name. */
  campaigns: DailySpendCampaign[];
  /** The cto-stats source rule, ORDER BY label, siteCode. */
  sources: DailySpendSourceRow[];
  /** The Facebook row (087-02); null when there is no Paid Social lead and no spend entry in range. */
  paidSocial: DailySpendPaidSocialRow | null;
  totals: DailySpendTotals;
}

/** Wire shape of one Display Ad Spend entry (task 055-01; source per 094-02). */
export interface DailySpendEntryResponse {
  date: string;
  amount: number;
  /** The entry's Display Ad source (`site.site_id`); null = the day's unsplit row. */
  siteId: string | null;
}

/**
 * Screen filter state (the query is `coopReportQuerySchema.pick({ from, to,
 * includeTest })` on the api side): `from`/`to` (`YYYY-MM-DD`) and
 * `includeTest` are always sent; `source` (094-03, D3) — `'all'`/absent = no
 * filter, else the pseudo-key (`facebook`, `unattributed`) or a `site.site_id`
 * — is sent unless `'all'` (byte-parity with the pre-epic request).
 */
export interface DailySpendStatsFilters {
  from: string;
  to: string;
  includeTest: boolean;
  source?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/**
 * Stats query string — `from`/`to` and `includeTest` as `'true' | 'false'`,
 * always sent; `source` only when set and not `'all'` (the pre-epic request
 * shape is unchanged under the default view).
 */
export function buildDailySpendStatsQuery(filters: DailySpendStatsFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  params.set('includeTest', filters.includeTest ? 'true' : 'false');
  if (filters.source !== undefined && filters.source !== 'all') {
    params.set('source', filters.source);
  }
  return params;
}

/** Absolute stats URL, ready for `fetch`. */
export function dailySpendStatsUrl(filters: DailySpendStatsFilters): string {
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/daily-spend-stats?${buildDailySpendStatsQuery(filters).toString()}`;
}

/** Absolute entry URL for one calendar day (the date rides the path). */
export function dailySpendEntryUrl(date: string): string {
  return `${API_BASE_URL}${ADMIN_PREFIX}/daily-spend-entries/${date}`;
}

/**
 * `message` of a Nest error body without a cast: narrow the unknown JSON with
 * the `in` operator, then the string check. Falls back to the HTTP status text.
 */
function messageFromBody(body: unknown, fallback: string): string {
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = body.message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}

/** Success envelope — matches the api's `ApiResponse<T>` (conventions §DTO). */
interface DailySpendStatsEnvelope {
  data: DailySpendStatsResponse;
}

interface DailySpendEntryEnvelope {
  data: DailySpendEntryResponse;
}

async function errorFrom(response: Response): Promise<Error> {
  const body: unknown = await response.json().catch(() => ({}));
  return new Error(messageFromBody(body, response.statusText));
}

/** Fetch the whole dashboard for one filter set. Non-2xx throws with the body's message. */
export async function fetchDailySpendStats(
  filters: DailySpendStatsFilters
): Promise<DailySpendStatsResponse> {
  const response = await apiFetch(dailySpendStatsUrl(filters));
  if (!response.ok) {
    throw await errorFrom(response);
  }
  const envelope: DailySpendStatsEnvelope = await response.json();
  return envelope.data;
}

/**
 * Upsert one day's Display Ad Spend (optionally per source — 094-02:
 * `siteId` null/absent writes the day's unsplit row). Non-2xx throws with the
 * body's message.
 */
export async function putDailySpendEntry(
  date: string,
  amount: number,
  siteId: string | null = null
): Promise<DailySpendEntryResponse> {
  const response = await apiFetch(dailySpendEntryUrl(date), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(siteId === null ? { amount } : { amount, siteId }),
  });
  if (!response.ok) {
    throw await errorFrom(response);
  }
  const envelope: DailySpendEntryEnvelope = await response.json();
  return envelope.data;
}

/**
 * Remove one day's Display Ad Spend (204, no body; optionally per source —
 * 094-02). Non-2xx throws with the body's message.
 */
export async function deleteDailySpendEntry(
  date: string,
  siteId: string | null = null
): Promise<void> {
  const response = await apiFetch(dailySpendEntryUrl(date), {
    method: 'DELETE',
    ...(siteId === null
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ siteId }) }),
  });
  if (!response.ok) {
    throw await errorFrom(response);
  }
}
